import React from 'react';
import styles from './Badge.module.css';

export type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

export interface BadgeProps {
  /**
   * Badge content/label
   */
  children: React.ReactNode;

  /**
   * Visual variant
   * @default 'default'
   */
  variant?: BadgeVariant;

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * ARIA label for screen readers (recommended for icon-only badges)
   */
  'aria-label'?: string;
}

/**
 * Badge Component
 *
 * Compact status indicator following Bauhaus design principles.
 * Uses grayscale values to communicate hierarchy without color dependency.
 *
 * Variants (grayscale semantic mapping):
 * - default: Gray-10 (standard emphasis)
 * - success: Gray-10 (highest emphasis - primary action complete)
 * - warning: Gray-7 (medium emphasis - attention needed)
 * - error: Gray-9 (high emphasis - critical state)
 * - info: Gray-8 (medium-high emphasis - informational)
 *
 * Design features:
 * - Inverted colors (dark background, light text) for contrast
 * - Uppercase text with wide tracking for readability
 * - Bold weight for visual hierarchy
 * - Compact padding for inline use
 * - No border radius (geometric purity)
 *
 * @example
 * ```tsx
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error">Failed</Badge>
 * <Badge variant="info">Processing</Badge>
 * ```
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  className,
  'aria-label': ariaLabel,
}) => {
  const variantClass = {
    default: styles.badgeDefault,
    success: styles.badgeSuccess,
    warning: styles.badgeWarning,
    error: styles.badgeError,
    info: styles.badgeInfo,
  }[variant];

  return (
    <span
      className={`${styles.badge} ${variantClass} ${className || ''}`}
      aria-label={ariaLabel}
    >
      {children}
    </span>
  );
};
